import {fromArrayBuffer} from '@planet/ol-numpytiles';
import {containsExtent, getArea, getIntersectionArea} from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import Polygon from 'ol/geom/Polygon';

import {getResolution} from './items.js';
import {toRadians} from './measure.js';
import {
  MAX_HIGH_RES_LIMITED_ACCESS_ZOOM,
  MAX_LIMITED_ACCESS_ZOOM,
  MAX_ZOOM,
} from '@explorer/constants';
import {ITEM_TYPES} from '@explorer/constants/item-types.js';

const geoJSONFormat = new GeoJSON();

const radius = 6378137;
const circumference = 2 * Math.PI * radius;
const maxResolution = circumference / 256;

export function getResolutionForZoom(zoom) {
  // return maxResolution / Math.pow(2, zoom);
  return 360.0 / (256 * Math.pow(2, zoom));
}

export function getZoomForResolution(resolution) {
  return Math.log(maxResolution / resolution) / Math.LN2;
}

// Reference: https://wiki.openstreetmap.org/wiki/Zoom_levels
export const getMetersPerPixel = (lat, zoom) => {
  // convert latitude to radians
  const latitude = toRadians(Math.abs(lat));
  const tileDistance = (circumference * Math.cos(latitude)) / Math.pow(2, zoom);
  return tileDistance / 256;
};

export const getWatermark = (extent, resolution, pixelRatio, size) => {
  const [width, height] = size;
  const patternCanvas = document.createElement('canvas');
  patternCanvas.width = width;
  patternCanvas.height = height;

  const context = patternCanvas.getContext('2d');

  context.globalAlpha = 0.65;
  context.strokeStyle = '#f5f5f5';
  context.lineWidth = 2;

  for (let i = 0; i < width; i++) {
    context.beginPath();
    context.moveTo(0, i * 24);
    context.lineTo(i * 24, 0);
    context.stroke();
  }

  context.globalAlpha = 0.8;

  context.fillStyle = '#fafafa';
  context.textAlign = 'center';

  // Adjust font to canvas size ... needed when canvas is just within an AOI
  let fontSize = 22;
  if (width < 600) {
    fontSize = 18;
  } else if (width < 960) {
    fontSize = 20;
  }

  context.font = `${fontSize * pixelRatio}px sans-serif`;
  context.lineWidth = 3;

  // Text checkered board
  // use the longer text as a measurement of "maxWidth"
  const textOffset = context.measureText('Full resolution available');
  const numCols = width / textOffset.width;
  const numRows = 4;

  // Use fontsize as a guesttimate of line height since actualBoundingBoxAscent is not supported in all browsers
  const vOffset = fontSize * 2.5;
  const hzOffset = textOffset.width / 2 + 8;

  for (let x = 0; x < numCols; x++) {
    for (let y = 0; y < numRows; y++) {
      const yy = (y * height) / numRows + vOffset;
      const xx = (x * width) / numCols + hzOffset;
      if (x % 2 !== y % 2) {
        continue;
      } else {
        context.fillText('Full resolution available', xx, yy);
        context.fillText('upon purchase', xx, yy + fontSize * 2);
      }
    }
  }

  return patternCanvas;
};

export const isExtentVisible = (visibleExtent, geomExtent, minArea = 0.2) => {
  const geomArea = getArea(geomExtent);
  const testArea = getIntersectionArea(visibleExtent, geomExtent) / geomArea;
  const viewWithin = containsExtent(geomExtent, visibleExtent);
  return viewWithin || testArea > minArea;
};

export const createGeometriesMask = (geometries, worldExteriorRing) => {
  if (!geometries || !geometries.length) {
    return null;
  }

  const rings = [];

  geometries.forEach(geometry => {
    const geometryObj = geoJSONFormat.readGeometry(geometry);

    if (worldExteriorRing) {
      // create a world-spanning exterior ring
      const extent = geometryObj.getExtent();
      const centerLon = (extent[0] + extent[2]) / 2;
      rings[0] = [
        [centerLon - 180, -90],
        [centerLon + 180, -90],
        [centerLon + 180, 90],
        [centerLon - 180, 90],
        [centerLon - 180, -90],
      ];
    }
    // use footprint as interior ring(s)
    const type = geometryObj.getType();
    const coordinates = geometryObj.getCoordinates();
    if (type === 'Polygon') {
      rings.push(coordinates[0].reverse());
    } else if (type === 'MultiPolygon') {
      coordinates.forEach(polyCoordinates => {
        rings.push(polyCoordinates[0].reverse());
      });
    } else {
      throw new Error(`Unexpected footprint type: ${type}`);
    }
  });
  return geoJSONFormat.writeGeometryObject(new Polygon(rings));
};

export const createGeometryMask = (geometry, worldExteriorRing = false) => {
  const geos = Array.isArray(geometry) ? geometry : [geometry];
  return createGeometriesMask(geos, worldExteriorRing);
};

export const getLimitedZoomForItem = item => {
  const isHighRes = [
    ITEM_TYPES.SKYSATSCENE,
    ITEM_TYPES.SKYSATCOLLECT,
    ITEM_TYPES.PELICANSCENE,
    ITEM_TYPES.TANAGERSCENE,
    ITEM_TYPES.TANAGERMETHANE,
  ].includes(item.properties.item_type);

  return isHighRes ? MAX_HIGH_RES_LIMITED_ACCESS_ZOOM : MAX_LIMITED_ACCESS_ZOOM;
};

export const getMaxZoomForItem = item => {
  let maxZoom = getLimitedZoomForItem(item);

  // ToDo: eventually this should only check canStream. Permissions are still conflated coming from admin
  if (item.properties.canDownload || item.properties.canStream) {
    maxZoom = getZoomForResolution(getResolution(item));
    if (isNaN(maxZoom) || maxZoom > MAX_ZOOM) {
      maxZoom = MAX_ZOOM;
    }
  }

  return maxZoom;
};

// TODO: Update the numpy loader to actually load things.
// const fromArrayBuffer = v => v;

export const createNumpyLoader = (urlTemplate, bandCount = 5) => {
  // Warning! Do not use => syntax for the loader, OpenLayers requires
  //  being able to bind this function.
  let tileRequest = 0;

  return function numpyTileLoader(z, x, y) {
    const url = urlTemplate
      .replace('{z}', z)
      .replace('{x}', x)
      .replace('{y}', y)
      .replace('tiles{0-3}', 'tiles' + (tileRequest % 4));

    tileRequest += 1;

    return fetch(url)
      .then(r => r.arrayBuffer())
      .then(buffer => fromArrayBuffer(buffer))
      .then(numpyData => {
        // flatten the numpy data
        const dataTile = new Float32Array(256 * 256 * bandCount);
        const missingAlpha = numpyData.shape[0] < bandCount;

        const bandSize = 256 * 256;
        for (let x = 0; x < 256; x++) {
          for (let y = 0; y < 256; y++) {
            const px = (x + y * 256) * bandCount;
            dataTile[px + 0] = numpyData.data[y * 256 + x];
            dataTile[px + 1] = numpyData.data[bandSize + y * 256 + x];
            dataTile[px + 2] = numpyData.data[bandSize * 2 + y * 256 + x];
            dataTile[px + 3] = numpyData.data[bandSize * 3 + y * 256 + x];
            if (missingAlpha) {
              // When the alpha band is missing, consider a pixel populated if _any_
              //  of the band values are greater than 0.
              dataTile[px + 4] =
                dataTile[px] +
                  dataTile[px + 1] +
                  dataTile[px + 2] +
                  dataTile[px + 3] >
                0
                  ? 1.0
                  : 0.0;
            } else {
              dataTile[px + 4] =
                numpyData.data[bandSize * 4 + y * 256 + x] > 0 ? 1.0 : 0;
            }
          }
        }
        return dataTile;
      });
  };
};

export const createNumpyStyle = pixelDepth => {
  const interpolateBand = bandSrc => [
    'interpolate',
    ['linear'],
    bandSrc,
    ['var', `bMin`],
    0,
    ['var', `bMax`],
    1.0,
  ];

  return {
    color: [
      'array',
      interpolateBand([
        'case',
        ['==', ['var', 'bandOrder'], 'rgba'],
        ['band', 1],
        ['band', 3],
      ]),
      interpolateBand(['band', 2]),
      interpolateBand([
        'case',
        ['==', ['var', 'bandOrder'], 'rgba'],
        ['band', 3],
        ['band', 1],
      ]),
      [
        'case',
        [
          '>',
          [
            'case',
            ['==', ['var', 'bandOrder'], 'rgba'],
            ['band', 4],
            ['band', 5],
          ],
          0,
        ],
        1.0,
        0,
      ],
    ],
    brightness: ['var', 'bx'],
    contrast: ['var', 'cx'],
    saturation: ['var', 'sx'],
    variables: {
      bMin: 0,
      bMax: pixelDepth,
      bx: 0,
      cx: 0,
      sx: 0,
      bandOrder: 'bgrna',
      pixelDepth,
    },
  };
};
